import React from 'react';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import PanToolIcon from '@mui/icons-material/PanTool';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { IPdfElement, PdfElementKind, TemplateMode } from '../../types/pdfTemplateTypes';

interface PdfToolRailProps {
  elementIcons: Record<PdfElementKind, React.ReactNode>;
  elementLabels: Record<PdfElementKind, string>;
  templateMode?: TemplateMode;
  panMode: boolean;
  selectedElement: IPdfElement | null;
  onAddElement: (kind: PdfElementKind) => void;
  onTogglePan: () => void;
  onDuplicateSelected: () => void;
  onToggleSelectedLock: () => void;
}

const TOOL_ORDER: PdfElementKind[] = [
  'staticText',
  'richText',
  'textField',
  'productImage',
  'staticImage',
  'rectangle',
  'line',
  'bulletList',
  'logo',
];

const PdfToolRail: React.FC<PdfToolRailProps> = ({
  elementIcons,
  elementLabels,
  templateMode,
  panMode,
  selectedElement,
  onAddElement,
  onTogglePan,
  onDuplicateSelected,
  onToggleSelectedLock,
}) => {
  const disablePageDataTools = templateMode === 'multi';

  return (
    <Paper
      sx={{
        width: 52,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 0.75,
        borderRadius: 2.5,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        background: 'rgba(255,255,255,0.95)',
      }}
    >
      <Tooltip title="Handverktyg (panorera)" placement="right">
        <IconButton size="small" color={panMode ? 'primary' : 'default'} onClick={onTogglePan}>
          <PanToolIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider flexItem sx={{ my: 0.5 }} />

      {TOOL_ORDER.map((kind) => {
        const disabled = disablePageDataTools && (kind === 'productImage' || kind === 'textField');
        return (
          <Tooltip
            key={kind}
            placement="right"
            title={disabled ? 'Produktdata på sidnivå är avstängd i multi-läge. Använd produktblock.' : elementLabels[kind]}
          >
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => onAddElement(kind)}>
                {elementIcons[kind]}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}

      <Box sx={{ flexGrow: 1 }} />

      <Divider flexItem sx={{ my: 0.5 }} />

      <Tooltip title="Duplicera markerat element" placement="right">
        <span>
          <IconButton size="small" disabled={!selectedElement} onClick={onDuplicateSelected}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={selectedElement?.locked ? 'Lås upp markerat element' : 'Lås markerat element'} placement="right">
        <span>
          <IconButton size="small" disabled={!selectedElement} onClick={onToggleSelectedLock}>
            {selectedElement?.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
};

export default PdfToolRail;
